/*
 * The color-by-number picture (ADR 0058 §1): a border collie as pixel art, one
 * character per cell, read top row first. Every cell is a problem, so every
 * colour on the finished sheet was earned by an answer.
 *
 * Kept as strings so it can be read - and redrawn - in the source.
 */

export type PaletteKey = "black" | "white" | "sky" | "grass" | "brown" | "pink";

export interface PaletteColor {
    key: PaletteKey;
    /** The fill on the answer page and the key's swatch. */
    fill: string;
    /** Answers in black cells are printed white on the answer page. */
    darkFill: boolean;
}

const CODES: Record<string, PaletteKey> = {
    B: "black",
    W: "white",
    ".": "sky",
    G: "grass",
    N: "brown",
    P: "pink",
};

/** In key order: the biggest areas first, so the key reads the way the picture fills. */
export const PALETTE: PaletteColor[] = [
    { key: "black", fill: "#1f2937", darkFill: true },
    { key: "white", fill: "#ffffff", darkFill: false },
    { key: "sky", fill: "#93c5fd", darkFill: false },
    { key: "grass", fill: "#4ade80", darkFill: false },
    { key: "brown", fill: "#92400e", darkFill: true },
    { key: "pink", fill: "#f9a8d4", darkFill: false },
];

const ROWS = [
    "............",
    "..B......B..",
    ".BBB....BBB.",
    ".BBBBBBBBBB.",
    "BBBBBWWBBBBB",
    "BBNBBWWBBNBB",
    "BBBBWWWWBBBB",
    ".BBWWBBWWBB.",
    ".BBWWWWWWBB.",
    "..BWWPPWWB..",
    "..BBWPPWBB..",
    ".BBBWWWWBBB.",
    "GBBWWWWWWBBG",
    "GBWWWWWWWWBG",
];

export const PICTURE_COLUMNS = ROWS[0].length;
export const PICTURE_ROWS = ROWS.length;

/** Each cell's colour, row by row. */
export const PICTURE_CELLS: PaletteKey[] = ROWS.flatMap((row) => row.split("").map((code) => CODES[code]));

/** How many problems one picture holds - the format's only per-page count. */
export const COLOR_BY_NUMBER_CELLS = PICTURE_CELLS.length;

export function paletteColor(key: string): PaletteColor {
    return PALETTE.find((color) => color.key === key) ?? PALETTE[0];
}
